import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "~/hooks/useAuth";
import { unwrap } from "@brad-tipper/app-frontend";

import { api } from "~/api/client";
import type { Notification } from "~/api/generated/types.gen";

export type AppNotification = Notification & { readAt: string | null };

const notificationQueryKey = (userId: string) =>
  ["notifications", userId] as const;

async function listNotifications() {
  const { items } = await unwrap(api.listNotifications());
  return items.map((notification) => ({
    ...notification,
    readAt: notification.read_at,
  })) satisfies AppNotification[];
}

export function useNotifications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? "";
  const query = useQuery({
    queryKey: notificationQueryKey(userId),
    queryFn: () => listNotifications(),
    enabled: Boolean(userId),
  });
  const markRead = useMutation({
    mutationFn: async (notificationId: string) => {
      if (!userId)
        throw new Error("You must be signed in to read notifications.");
      await unwrap(api.markNotificationRead({ path: { id: notificationId } }));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: notificationQueryKey(userId),
      });
    },
  });

  const notifications = query.data ?? [];
  return {
    notifications,
    unreadCount: notifications.filter((notification) => !notification.readAt)
      .length,
    loading: query.isLoading,
    error: query.error ?? markRead.error,
    markRead: markRead.mutateAsync,
    markReadPending: markRead.isPending,
  };
}
